import {
  CompletePublicIntakeRequestSchema,
  encodeStrictBase64,
  isPublicIntakeAlreadySubmitted,
  type LockedBookingIdentity,
  lockedBookingIdentity,
  ManagePublicAppointmentRequestSchema,
  OpenPublicAppointmentTokenRequestSchema,
  type PatientCancellationReason,
  type PatientIntakeAnswers,
  PUBLIC_CHANGE_CUTOFF_PASSED_ERROR_CODE,
  PUBLIC_HEALTH_CARD_PHOTO_LIMIT_ERROR_CODE,
  PUBLIC_HEALTH_CARD_PHOTOS_UNAVAILABLE_ERROR_CODE,
  PUBLIC_INTAKE_ALREADY_SUBMITTED_ERROR_CODE,
  PUBLIC_TERMS_CHANGED_ERROR_CODE,
  PublicAppointmentMutationReceiptSchema,
  type PublicAppointmentOpenResponse,
  PublicAppointmentOpenResponseSchema,
  type PublicAppointmentSession,
  type PublicAvailabilitySlot,
  type PublicConsentRequirement,
  PublicHealthCardPhotoReceiptSchema,
  PublicHealthCardPhotoUploadRequestSchema,
  type PublicIntakeConsent,
  PublicIntakeReceiptSchema,
  PublicManageAvailabilityRequestSchema,
  PublicManageAvailabilityResponseSchema,
  PublicRescheduleRequestSchema,
  type PublicRescheduleResponse,
  PublicRescheduleResponseSchema,
  type PublicVisitKind,
  publicSessionQuestionnaireSections,
  publicSessionVisitKind,
} from "@emr/contracts";
import { useQueryClient } from "@tanstack/react-query";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { App } from "../App";
import { termsChanged } from "../consent/consentChoices";
import { emptyIntake } from "../data/intake";
import type { IconName } from "../Icon";
import { answersNotAccepted, appointmentChanged, isSchemaError } from "../lib/validation";
import { ManageAppointment, type ManageOutcome } from "../manage/ManageAppointment";
import { RescheduledAppointment } from "../manage/RescheduledAppointment";
import type { ManageTimesSearch } from "../manage/RescheduleTimes";
import type { IntakeData } from "../types";
import type { HealthCardPhotoSendOutcome } from "./HealthCardPhoto";
import { DateOfBirthForm, PatientTopBar, StateMark } from "./PatientChrome";
import { type PublicAppointmentLink, takePublicPatientLink } from "./publicPatientLink";

/**
 * The one refusal for a link that cannot be opened. The service answers a wrong date of birth,
 * and an expired, replaced, revoked or used link, the same way, so the page cannot tell which
 * and names both.
 */
export const linkUnavailable =
  "This link could not be opened. Check your date of birth. If it is right, the link may have expired or been replaced: ask the clinic for a new one.";

/** The link stopped working while the patient was answering (it expired, or the visit ended). */
function linkExpired(visitKind: PublicVisitKind): string {
  return `This link has expired or was replaced, so your answers were not saved. Ask ${
    visitKind === "walk_in" ? "the front desk" : "the clinic"
  } for a new link.`;
}

/** The request may or may not have reached the clinic; the questionnaire keeps every answer. */
export const answersMayNotHaveArrived =
  "Your answers may not have reached the clinic. Check your connection, then submit again. Sending them again is safe: the clinic keeps one copy.";
const clinicUnreachable = "The clinic could not be reached. Check your connection and try again.";
/** A walk-in's visit ended or changed under the open questionnaire. */
const walkInVisitChanged = "This visit has changed. Ask the front desk for a new link.";

/** "call the clinic", with its phone when the session has one. */
function callTheClinic(session: PublicAppointmentSession): string {
  return session.clinicPhone === undefined
    ? "call the clinic"
    : `call the clinic at ${session.clinicPhone}`;
}

/** A cancel or time change came inside the clinic's cutoff (PA-9); nothing changed. */
function changesClosed(session: PublicAppointmentSession): string {
  return `Online changes have closed for this appointment. To cancel or change the time, ${callTheClinic(session)}.`;
}

/** Time changes stopped being offered while the page was open: the cutoff, or the clinic's rules. */
function movingClosed(session: PublicAppointmentSession): string {
  return session.selfService?.cancel === "past_cutoff"
    ? changesClosed(session)
    : `Your appointment can no longer be moved online. To change the time, ${callTheClinic(session)}.`;
}

/** The booking changed between opening the page and the request; nothing was saved. */
const appointmentChangedMeanwhile =
  "Your appointment changed after this page opened. Check the details, then try again.";

/** The time the patient picked was taken meanwhile; the booking kept its time. */
const timeTaken = "That time was just taken. Choose another time.";

/**
 * A time change or cancel was sent and no readable answer came back (lead decision L5): it may
 * have been saved, and there is no replay, so the clinic is the check.
 */
function rescheduleMayNotHaveArrived(session: PublicAppointmentSession): string {
  return `Your new time may not have been saved. Check your connection and try again. If this page didn't update, ${callTheClinic(session)} to check your appointment.`;
}
function rescheduleLinkGone(session: PublicAppointmentSession): string {
  return `This link no longer works. If you just changed the time, the change may have been saved: ${callTheClinic(session)} to check your appointment.`;
}
function cancelMayNotHaveArrived(session: PublicAppointmentSession): string {
  return `Your cancellation may not have been saved. Check your connection and try again. If this page didn't update, ${callTheClinic(session)} to check your appointment.`;
}
function cancelLinkGone(session: PublicAppointmentSession): string {
  return `This link no longer works. If you just cancelled, the cancellation may have been saved: ${callTheClinic(session)} to check your appointment.`;
}

/** A time change's result, held in memory only: the new links never enter the address. */
export interface Rescheduled {
  readonly response: PublicRescheduleResponse;
  /** The session the change was made from: the clinic, visit type and clock. */
  readonly session: PublicAppointmentSession;
  /** Who the new time is with, as the times named them; null when the clinic assigns one. */
  readonly physician: string | null;
}

interface FinishedMark {
  readonly icon: IconName;
  readonly tone: "info" | "done" | "problem";
}
const openingMark: FinishedMark = { icon: "lock", tone: "info" };
const doneMark: FinishedMark = { icon: "check", tone: "done" };
const refusedMark: FinishedMark = { icon: "info", tone: "info" };

/** What the page shows once the link has done its work: no form, no answers. */
interface Finished {
  readonly heading: string;
  readonly message: string;
  /**
   * The mark above the heading: a check unless set. A refusal that saved nothing (answers sent
   * again) is not shown as a success.
   */
  readonly mark?: FinishedMark;
  /** A time change (PA-9): the new time, its calendar file and the new links. */
  readonly rescheduled?: Rescheduled;
}

/**
 * A time change's page once the session ends (idle, the page hidden, or restored from history): the
 * new links and the appointment go with it, since the device may be shared.
 */
function rescheduleEnded({ session }: Rescheduled): Finished {
  return {
    heading: "Your appointment time changed",
    message: `Your new time is saved. This page closed to keep your appointment private. For your new link, ${callTheClinic(session)}.`,
  };
}

/** A confirm from a server before PA-9, whose confirm used the link up. */
const confirmedFinished: Finished = {
  heading: "Saved",
  message: "Your appointment is confirmed.",
};

/**
 * The patient is already in the clinic: a walk-in, or a booking the desk checked in and then
 * sent a questionnaire link to (PA-5). They are not told to check in again.
 */
function patientIsAtClinic(session: PublicAppointmentSession): boolean {
  return (
    publicSessionVisitKind(session) === "walk_in" ||
    session.status === "checked_in" ||
    session.status === "in_progress"
  );
}

function submitted(atClinic: boolean): Finished {
  return atClinic
    ? {
        heading: "Answers sent",
        message:
          "Thanks. Your answers are with the front desk. Please stay in the waiting room; staff will call you.",
      }
    : {
        heading: "Questionnaire sent",
        message: "Your questionnaire has been submitted. Check in with staff when you arrive.",
      };
}

/**
 * The questionnaire was already submitted (lead decision H2). Opening the link again says so and
 * never shows the answers; answers sent from a second device, or changed after the first submit,
 * were not saved, so the page never tells that device its answers arrived.
 */
function alreadySubmitted(visitKind: PublicVisitKind, refusedHere: boolean): Finished {
  const desk = visitKind === "walk_in" ? "the front desk" : "the clinic";
  return {
    heading: "Already submitted",
    // The answers on this device were refused: not a success.
    ...(refusedHere ? { mark: refusedMark } : {}),
    message: refusedHere
      ? `This questionnaire was already submitted, so the answers on this device were not saved. If something has changed, tell ${desk}.`
      : `This questionnaire was already submitted. You don't need to fill it in again. If something has changed, tell ${desk}.`,
  };
}

/** The clinic published new terms after the questionnaire opened; nothing was saved. */
class TermsChangedError extends Error {
  public constructor() {
    super(termsChanged);
    this.name = "TermsChangedError";
  }
}

/** The link's questionnaire was already submitted and this submit was not its exact retry. */
class IntakeAlreadySubmittedError extends Error {
  public constructor() {
    super("This questionnaire was already submitted.");
    this.name = "IntakeAlreadySubmittedError";
  }
}

/** The request never got an answer: it may or may not have reached the clinic. */
class ConnectionLostError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "ConnectionLostError";
  }
}

/** A cancel or time change inside the clinic's cutoff (PA-9): nothing changed, the link works. */
class ChangeCutoffPassedError extends Error {
  public constructor() {
    super("Online changes have closed for this appointment.");
    this.name = "ChangeCutoffPassedError";
  }
}

/** A 409 with no code the page handles: the appointment or the time changed meanwhile. */
class ConflictError extends Error {
  public constructor(message: string) {
    super(message);
    this.name = "ConflictError";
  }
}

/** The service's error code (for example TERMS_CHANGED), never its message text. */
async function errorCode(response: Response): Promise<string | undefined> {
  const body = (await response.json().catch(() => null)) as {
    readonly error?: { readonly code?: unknown };
  } | null;
  const code = body?.error?.code;
  return typeof code === "string" ? code : undefined;
}

/** Patient-safe text for a refusal, by what the request was for. */
interface RefusalText {
  /**
   * Replaces the generic text for a 400 or 422: only a request that carries the patient's answers
   * can be refused for them, so open and manage keep the generic message.
   */
  readonly rejectedAnswers?: string;
  /** Replaces the generic text for a 404: a link that worked a moment ago has stopped working. */
  readonly notFound?: string;
  /** Replaces the generic text when the service could not be reached. */
  readonly unreachable?: string;
  /** Replaces "this appointment has changed" for a 409 that carries no code the page handles. */
  readonly conflict?: string;
  /**
   * Treats a server failure (5xx) as a lost answer with this text: the request may have been
   * saved before it failed.
   */
  readonly serverFailed?: string;
}

/** The clinic's public API address for a path. Credentials never go in it, only in bodies. */
function publicApiUrl(clinicSlug: string, path: string): string {
  const origin = String(import.meta.env.VITE_AGENT_API_ORIGIN ?? "http://127.0.0.1:4100").replace(
    /\/$/,
    "",
  );
  return `${origin}/v1/public/${encodeURIComponent(clinicSlug)}${path}`;
}

/** Posts to the clinic's public API and turns a refusal into patient-safe text. */
async function post(
  clinicSlug: string,
  path: string,
  body: unknown,
  signal: AbortSignal,
  text: RefusalText = {},
) {
  let response: Response;
  try {
    response = await fetch(publicApiUrl(clinicSlug, path), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal,
    });
  } catch (caught) {
    // An abort is the page's own doing (the session ended), not a lost connection.
    if (signal.aborted) throw caught;
    throw new ConnectionLostError(text.unreachable ?? clinicUnreachable);
  }
  if (!response.ok) {
    const code = response.status === 409 ? await errorCode(response) : undefined;
    if (code === PUBLIC_TERMS_CHANGED_ERROR_CODE) throw new TermsChangedError();
    if (code === PUBLIC_INTAKE_ALREADY_SUBMITTED_ERROR_CODE)
      throw new IntakeAlreadySubmittedError();
    if (code === PUBLIC_CHANGE_CUTOFF_PASSED_ERROR_CODE) throw new ChangeCutoffPassedError();
    if (response.status === 409) throw new ConflictError(text.conflict ?? appointmentChanged);
    if (response.status >= 500 && text.serverFailed !== undefined) {
      throw new ConnectionLostError(text.serverFailed);
    }
    throw new Error(
      response.status === 429
        ? "Too many attempts. Please wait before trying again."
        : text.rejectedAnswers !== undefined && (response.status === 400 || response.status === 422)
          ? text.rejectedAnswers
          : response.status === 404 && text.notFound !== undefined
            ? text.notFound
            : linkUnavailable,
    );
  }
  return response.json();
}

/**
 * Sends a health card photo (PA-13) and says what became of it; never throws. A 409 names why
 * the clinic is not taking it; a refused body (400, 413, 415) asks for another photo; anything
 * else (no answer, 404, 429, 5xx) may be tried again.
 */
async function postHealthCardPhoto(
  clinicSlug: string,
  body: unknown,
  signal: AbortSignal,
): Promise<HealthCardPhotoSendOutcome> {
  let response: Response;
  try {
    response = await fetch(publicApiUrl(clinicSlug, "/intake/health-card-photo"), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      cache: "no-store",
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal,
    });
  } catch {
    return "not_sent";
  }
  if (response.ok) {
    const receipt = await response.json().catch(() => null);
    return PublicHealthCardPhotoReceiptSchema.safeParse(receipt).success ? "sent" : "not_sent";
  }
  if (response.status === 409) {
    const code = await errorCode(response);
    if (code === PUBLIC_HEALTH_CARD_PHOTOS_UNAVAILABLE_ERROR_CODE) return "unavailable";
    if (code === PUBLIC_HEALTH_CARD_PHOTO_LIMIT_ERROR_CODE) return "limit";
    return "not_sent";
  }
  return [400, 413, 415].includes(response.status) ? "unreadable" : "not_sent";
}

export function PublicPatientLane({
  link,
  idleTimeoutMs = 10 * 60_000,
}: {
  readonly link: PublicAppointmentLink;
  readonly idleTimeoutMs?: number;
}) {
  const queryClient = useQueryClient();
  const token = useRef(link.token);
  const verifiedDob = useRef("");
  const operation = useRef<AbortController | null>(null);
  // A health card photo on its way (PA-13): its own request, beside any other operation.
  const photoUpload = useRef<AbortController | null>(null);
  const generation = useRef(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [session, setSession] = useState<PublicAppointmentSession | null>(null);
  // What the questionnaire asks the patient to agree to; reloaded when the clinic's terms change.
  const [consentDocuments, setConsentDocuments] = useState<PublicConsentRequirement | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [closed, setClosed] = useState(link.token === null);
  const [done, setDone] = useState<Finished | null>(null);
  // What the last manage request did, such as a confirm that kept the link (PA-9).
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  // One key for every submit of this link's questionnaire: a retry after a lost response, with
  // the same answers, is the same submit, and the service answers it with the first one's
  // receipt instead of saving a second copy.
  const intakeKey = useRef(crypto.randomUUID());
  /**
   * Ends the session. A photo still on its way when the questionnaire is submitted keeps going
   * (keepPhotoUpload): stopping it would lose the photo without a word. Every other ending (idle,
   * the page hidden or restored, End session) stops it.
   */
  const endSession = useCallback(
    (keepPhotoUpload: boolean) => {
      generation.current += 1;
      operation.current?.abort();
      if (!keepPhotoUpload) photoUpload.current?.abort();
      token.current = null;
      verifiedDob.current = "";
      setDateOfBirth("");
      setSession(null);
      setConsentDocuments(null);
      setError(null);
      setBusy(false);
      setStatusMessage(null);
      setDone((current) =>
        current?.rescheduled === undefined ? current : rescheduleEnded(current.rescheduled),
      );
      setClosed(true);
      // Every way the lane ends comes through here, and most of them unmount the questionnaire
      // without its own reset, so the submission it holds must be cleared here too.
      queryClient.clear();
    },
    [queryClient],
  );
  const close = useCallback(() => endSession(false), [endSession]);
  /** Ends the session and shows what the link did, in place of anything it showed before. */
  const finish = useCallback(
    (finished: Finished) => {
      endSession(true);
      setDone(finished);
    },
    [endSession],
  );

  useEffect(() => {
    document.title = "Secure appointment link · Bob Doc";
    let timeout: ReturnType<typeof setTimeout>;
    const activity = () => {
      clearTimeout(timeout);
      timeout = setTimeout(close, idleTimeoutMs);
    };
    const restored = (event: PageTransitionEvent) => {
      if (event.persisted) close();
    };
    activity();
    window.addEventListener("pointerdown", activity);
    window.addEventListener("keydown", activity);
    window.addEventListener("pagehide", close);
    window.addEventListener("pageshow", restored);
    return () => {
      clearTimeout(timeout);
      operation.current?.abort();
      photoUpload.current?.abort();
      window.removeEventListener("pointerdown", activity);
      window.removeEventListener("keydown", activity);
      window.removeEventListener("pagehide", close);
      window.removeEventListener("pageshow", restored);
    };
  }, [close, idleTimeoutMs]);

  // The questionnaire that held focus is gone: take it to the heading that says what happened.
  // A layout effect, so focus moves in the same commit that shows the heading: a submit answers
  // asynchronously, and a passive effect could run only after the page was already read.
  useLayoutEffect(() => {
    if (done !== null) headingRef.current?.focus();
  }, [done]);

  // A link opened in this tab again only changes the address fragment, so the page is not
  // reloaded and main.tsx never sees it: take its token here, and strip it from the address
  // either way. An open session keeps what the patient is doing rather than being replaced.
  useEffect(() => {
    const takeNewLink = () => {
      const next = takePublicPatientLink();
      if (next === null || next.token === null || session !== null || operation.current) return;
      if (next.clinicSlug !== link.clinicSlug || next.purpose !== link.purpose) return;
      token.current = next.token;
      intakeKey.current = crypto.randomUUID();
      setDone(null);
      setError(null);
      setClosed(false);
    };
    window.addEventListener("hashchange", takeNewLink);
    return () => window.removeEventListener("hashchange", takeNewLink);
  }, [link.clinicSlug, link.purpose, session]);

  /**
   * Reads the session again for the current terms; the token is not used up by opening it. An
   * intake link whose questionnaire was already submitted answers that, and nothing more.
   */
  const openSession = async (
    dob: string,
    signal: AbortSignal,
  ): Promise<PublicAppointmentOpenResponse> => {
    if (!token.current) throw new Error(linkUnavailable);
    const body = OpenPublicAppointmentTokenRequestSchema.parse({
      token: token.current,
      purpose: link.purpose,
      dateOfBirth: dob,
    });
    const result = PublicAppointmentOpenResponseSchema.parse(
      await post(link.clinicSlug, "/access/open", body, signal),
    );
    if (result.purpose !== link.purpose) throw new Error(linkUnavailable);
    return result;
  };

  const open = async (event: FormEvent) => {
    event.preventDefault();
    if (!token.current || busy || operation.current) return;
    const controller = new AbortController();
    operation.current = controller;
    const current = generation.current;
    setBusy(true);
    setError(null);
    try {
      const result = await openSession(dateOfBirth, controller.signal);
      if (current !== generation.current || controller.signal.aborted) return;
      if (isPublicIntakeAlreadySubmitted(result)) {
        finish(alreadySubmitted(result.visitKind, false));
        return;
      }
      verifiedDob.current = dateOfBirth;
      setDateOfBirth("");
      setSession(result);
      setConsentDocuments(result.consentDocuments);
    } catch (caught) {
      if (current === generation.current && !controller.signal.aborted) {
        setError(
          caught instanceof Error && !isSchemaError(caught) ? caught.message : linkUnavailable,
        );
      }
    } finally {
      if (operation.current === controller) operation.current = null;
      if (current === generation.current) setBusy(false);
    }
  };

  /**
   * Runs one manage request at a time (PA-9). A refusal the request does not handle itself is
   * shown as patient-safe text, and the page stays where it was so the patient can try again.
   */
  const manageRequest = async (
    request: (
      signal: AbortSignal,
      isCurrent: () => boolean,
      opened: PublicAppointmentSession,
      linkToken: string,
    ) => Promise<ManageOutcome>,
  ): Promise<ManageOutcome> => {
    const opened = session;
    const linkToken = token.current;
    if (!linkToken || !opened || busy || operation.current) return "stay";
    const controller = new AbortController();
    operation.current = controller;
    const current = generation.current;
    const isCurrent = () => current === generation.current && !controller.signal.aborted;
    setBusy(true);
    setError(null);
    setStatusMessage(null);
    try {
      return await request(controller.signal, isCurrent, opened, linkToken);
    } catch (caught) {
      if (isCurrent()) {
        setError(
          caught instanceof Error && !isSchemaError(caught) ? caught.message : linkUnavailable,
        );
      }
      return "stay";
    } finally {
      if (operation.current === controller) operation.current = null;
      if (current === generation.current) setBusy(false);
    }
  };

  /**
   * After a refusal that changed nothing (the cutoff passed, or the booking changed), shows the
   * appointment as it is now, which names what the patient can still do, with why. False when
   * the link no longer opens: the clinic changed or replaced the booking meanwhile.
   */
  const showCurrent = async (
    signal: AbortSignal,
    isCurrent: () => boolean,
    message: (reopened: PublicAppointmentSession) => string,
  ): Promise<boolean> => {
    let reopened: PublicAppointmentOpenResponse;
    try {
      reopened = await openSession(verifiedDob.current, signal);
    } catch {
      if (isCurrent()) setError(appointmentChanged);
      return false;
    }
    if (!isCurrent() || isPublicIntakeAlreadySubmitted(reopened)) return false;
    setSession(reopened);
    setError(message(reopened));
    return true;
  };

  const manageBody = (
    opened: PublicAppointmentSession,
    linkToken: string,
    action: "confirm" | "cancel",
    reason?: PatientCancellationReason,
  ) =>
    ManagePublicAppointmentRequestSchema.parse({
      token: linkToken,
      dateOfBirth: verifiedDob.current,
      idempotencyKey: crypto.randomUUID(),
      expectedVersion: opened.version,
      action,
      ...(reason === undefined ? {} : { cancellationReason: reason }),
    });

  /** Confirm keeps the link working (PA-9): the page reads the appointment again and stays. */
  const confirmAppointment = () =>
    manageRequest(async (signal, isCurrent, opened, linkToken) => {
      try {
        PublicAppointmentMutationReceiptSchema.parse(
          await post(
            link.clinicSlug,
            "/appointments/manage",
            manageBody(opened, linkToken, "confirm"),
            signal,
          ),
        );
      } catch (caught) {
        if (!(caught instanceof ConflictError)) throw caught;
        await showCurrent(signal, isCurrent, () => appointmentChangedMeanwhile);
        return "details";
      }
      if (!isCurrent()) return "stay";
      // A server from before PA-9 used the link up on a confirm.
      if (opened.selfService === undefined) {
        finish(confirmedFinished);
        return "details";
      }
      const reopened = await openSession(verifiedDob.current, signal).catch(() => null);
      if (!isCurrent()) return "stay";
      if (reopened === null || isPublicIntakeAlreadySubmitted(reopened)) {
        finish(confirmedFinished);
        return "details";
      }
      setSession(reopened);
      setStatusMessage("Your appointment is confirmed.");
      return "details";
    });

  /** Cancel uses the link up; the reason is one of the five, and none to a server before PA-9. */
  const cancelAppointment = (reason: PatientCancellationReason | undefined) =>
    manageRequest(async (signal, isCurrent, opened, linkToken) => {
      try {
        PublicAppointmentMutationReceiptSchema.parse(
          await post(
            link.clinicSlug,
            "/appointments/manage",
            manageBody(opened, linkToken, "cancel", reason),
            signal,
            {
              notFound: cancelLinkGone(opened),
              unreachable: cancelMayNotHaveArrived(opened),
              serverFailed: cancelMayNotHaveArrived(opened),
            },
          ),
        );
      } catch (caught) {
        if (caught instanceof ChangeCutoffPassedError) {
          await showCurrent(signal, isCurrent, changesClosed);
          return "details";
        }
        if (caught instanceof ConflictError) {
          await showCurrent(signal, isCurrent, () => appointmentChangedMeanwhile);
          return "details";
        }
        throw caught;
      }
      if (!isCurrent()) return "stay";
      finish({
        heading: "Appointment cancelled",
        message: "Your appointment is cancelled. This link no longer works.",
      });
      return "details";
    });

  /** The times the booking can move to: read only, the link is not used up. */
  const loadTimes = async (search: ManageTimesSearch, signal: AbortSignal) => {
    if (!token.current) throw new Error(linkUnavailable);
    const body = PublicManageAvailabilityRequestSchema.parse({
      token: token.current,
      dateOfBirth: verifiedDob.current,
      fromDate: search.fromDate,
      toDate: search.toDate,
      ...(search.providerId === undefined ? {} : { providerId: search.providerId }),
    });
    try {
      return PublicManageAvailabilityResponseSchema.parse(
        await post(link.clinicSlug, "/appointments/manage/availability", body, signal),
      );
    } catch (caught) {
      // The clinic's rules closed online time changes meanwhile: show what the patient can do.
      if (
        !signal.aborted &&
        (caught instanceof ChangeCutoffPassedError || caught instanceof ConflictError)
      ) {
        void manageRequest(async (requestSignal, isCurrent) => {
          await showCurrent(requestSignal, isCurrent, movingClosed);
          return "details";
        });
      }
      throw caught;
    }
  };

  /**
   * Moves the booking (PA-9). It uses the link up and the response carries the new links, once:
   * they live only in memory and on the page's own links. A lost answer may have been saved
   * (lead decision L5), so the page says to check with the clinic.
   */
  const rescheduleAppointment = (slot: PublicAvailabilitySlot, physician: string | null) =>
    manageRequest(async (signal, isCurrent, opened, linkToken) => {
      const body = PublicRescheduleRequestSchema.parse({
        token: linkToken,
        dateOfBirth: verifiedDob.current,
        idempotencyKey: crypto.randomUUID(),
        expectedVersion: opened.version,
        startAt: slot.startAt,
        ...(slot.providerId === undefined ? {} : { providerId: slot.providerId }),
      });
      let answer: unknown;
      try {
        answer = await post(link.clinicSlug, "/appointments/reschedule", body, signal, {
          notFound: rescheduleLinkGone(opened),
          unreachable: rescheduleMayNotHaveArrived(opened),
          serverFailed: rescheduleMayNotHaveArrived(opened),
        });
      } catch (caught) {
        if (caught instanceof ChangeCutoffPassedError) {
          await showCurrent(signal, isCurrent, changesClosed);
          return "details";
        }
        if (caught instanceof ConflictError) {
          const shown = await showCurrent(signal, isCurrent, (reopened) =>
            reopened.selfService?.reschedule === "available" ? timeTaken : movingClosed(reopened),
          );
          return shown ? "times" : "details";
        }
        throw caught;
      }
      const response = PublicRescheduleResponseSchema.safeParse(answer);
      // An answer the page cannot read still followed a move that may have been saved.
      if (!response.success) throw new Error(rescheduleMayNotHaveArrived(opened));
      if (!isCurrent()) return "stay";
      finish({
        heading: "Your appointment time changed",
        message: "Your new time is saved.",
        rescheduled: { response: response.data, session: opened, physician },
      });
      return "details";
    });

  const submitIntake = async (
    intake: PatientIntakeAnswers,
    consent: PublicIntakeConsent | undefined,
  ) => {
    if (!token.current || !session || operation.current) throw new Error(linkUnavailable);
    const controller = new AbortController();
    operation.current = controller;
    const current = generation.current;
    // Read before anything ends the session.
    const visitKind = publicSessionVisitKind(session);
    const atClinic = patientIsAtClinic(session);
    try {
      const body = CompletePublicIntakeRequestSchema.parse({
        token: token.current,
        dateOfBirth: verifiedDob.current,
        // Nothing is saved on a refusal, so a retry after new terms keeps the same key; and a
        // retry of a submit whose response was lost is the same submit (see intakeKey).
        idempotencyKey: intakeKey.current,
        intake,
        ...(consent === undefined ? {} : { consent }),
      });
      const receipt = PublicIntakeReceiptSchema.parse(
        await post(link.clinicSlug, "/intake/complete", body, controller.signal, {
          rejectedAnswers: answersNotAccepted,
          notFound: linkExpired(visitKind),
          unreachable: answersMayNotHaveArrived,
          ...(visitKind === "walk_in" ? { conflict: walkInVisitChanged } : {}),
        }),
      );
      if (current !== generation.current || controller.signal.aborted)
        throw new Error(linkUnavailable);
      finish(submitted(atClinic));
      return { receipt: receipt.bookingReference, notice: receipt.notice };
    } catch (caught) {
      if (current === generation.current && !controller.signal.aborted) {
        if (caught instanceof IntakeAlreadySubmittedError) {
          finish(alreadySubmitted(visitKind, true));
        } else if (caught instanceof TermsChangedError) {
          // Show the new terms beside the answers, which stay as they are, and ask again.
          const reopened = await openSession(verifiedDob.current, controller.signal).catch(() => {
            throw new Error(linkUnavailable);
          });
          if (current !== generation.current || controller.signal.aborted)
            throw new Error(linkUnavailable);
          if (isPublicIntakeAlreadySubmitted(reopened)) {
            finish(alreadySubmitted(reopened.visitKind, true));
          } else {
            setConsentDocuments(reopened.consentDocuments);
          }
        }
      }
      throw caught;
    } finally {
      if (operation.current === controller) operation.current = null;
    }
  };

  /**
   * Sends a health card photo for this link (PA-13), with the token and verified date of birth
   * in the body. A newer photo replaces one still on its way. The questionnaire never waits for
   * it, and a submit does not stop it (see endSession).
   */
  const sendHealthCardPhoto = useCallback(
    async (jpeg: Uint8Array): Promise<HealthCardPhotoSendOutcome> => {
      const linkToken = token.current;
      if (!linkToken || verifiedDob.current === "") return "not_sent";
      photoUpload.current?.abort();
      const controller = new AbortController();
      photoUpload.current = controller;
      try {
        const body = PublicHealthCardPhotoUploadRequestSchema.safeParse({
          token: linkToken,
          dateOfBirth: verifiedDob.current,
          image: encodeStrictBase64(jpeg),
        });
        if (!body.success) return "unreadable";
        return await postHealthCardPhoto(link.clinicSlug, body.data, controller.signal);
      } finally {
        if (photoUpload.current === controller) photoUpload.current = null;
      }
    },
    [link.clinicSlug],
  );

  // An online booking's questionnaire link also carries the sex, mobile number and email the
  // patient typed when booking (the server sends them for no other link). The questionnaire
  // shows those its own fields accept read-only; anything else, sex "unknown" included, stays an
  // ordinary blank answer. A manage session never uses them.
  const lockedIdentity = useMemo<LockedBookingIdentity>(
    () => (session?.purpose === "intake" ? lockedBookingIdentity(session.bookingIdentity) : {}),
    [session],
  );
  // The questionnaire starts blank but for the date of birth the patient just verified, which
  // the server requires it to repeat, and the booking's locked values (App fills those in).
  const initialIntake = useMemo<IntakeData>(
    () => ({ ...emptyIntake, dateOfBirth: session === null ? "" : verifiedDob.current }),
    [session],
  );

  if (session?.purpose === "intake") {
    return (
      <App
        initialIntake={initialIntake}
        lockedIdentity={lockedIdentity}
        scheduledContext={session}
        consentDocuments={consentDocuments ?? session.consentDocuments}
        // Only the sections the link asks for: a walk-in whose chart existed before this visit
        // is not asked About you (PA-5).
        questionnaireSections={publicSessionQuestionnaireSections(session)}
        visitKind={publicSessionVisitKind(session)}
        atClinic={patientIsAtClinic(session)}
        submitIntake={submitIntake}
        persisted
        onSessionEnd={close}
        healthCardPhoto={sendHealthCardPhoto}
      />
    );
  }

  // The manage page (an open appointment, or its new time) keeps the manage card; every other
  // screen of the link is the same small centred card as the questionnaire's.
  const managing = session !== null || done?.rescheduled !== undefined;
  const mark = done === null ? openingMark : (done.mark ?? doneMark);
  return (
    <div className="patient-app">
      <PatientTopBar
        label={link.purpose === "intake" ? "Secure questionnaire" : "Secure appointment"}
        onEndSession={closed ? undefined : close}
      />
      <main className={managing ? "booking-state-card manage-card" : "intake-state"}>
        {managing ? null : <StateMark icon={mark.icon} tone={mark.tone} />}
        <h1 ref={headingRef} tabIndex={-1}>
          {done
            ? done.heading
            : closed
              ? "This session is closed"
              : session
                ? session.clinicName
                : link.purpose === "intake"
                  ? "Open your questionnaire"
                  : "Open your appointment"}
        </h1>
        {done ? (
          <>
            <p role="status">{done.message}</p>
            {done.rescheduled === undefined ? null : (
              <RescheduledAppointment {...done.rescheduled} />
            )}
          </>
        ) : closed ? (
          <p>Reopen the original link or ask the clinic for a new one.</p>
        ) : session ? (
          <ManageAppointment
            session={session}
            busy={busy}
            statusMessage={statusMessage}
            error={error}
            actions={{
              confirm: confirmAppointment,
              cancel: cancelAppointment,
              loadTimes,
              reschedule: rescheduleAppointment,
              clearError: () => setError(null),
            }}
          />
        ) : (
          <>
            <p>For your privacy, confirm your date of birth.</p>
            {/* A refusal can only come while the link is opening (the manage page shows its own,
                and closing or finishing clears it), so it shows in the form, under the field. */}
            <DateOfBirthForm
              value={dateOfBirth}
              onChange={setDateOfBirth}
              onSubmit={(event) => void open(event)}
              busy={busy}
              error={error}
            />
          </>
        )}
      </main>
    </div>
  );
}
